// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// Import style for web component
import styles from './web-component-style.scss?inline';

@customElement( 'uo-field-input-switch' ) // eslint-disable-line require-jsdoc
export class AutomatorFieldInputSwitch extends LitElement {
	// Define style
	static styles = css`${unsafeCSS( styles )}`;

	/**
	 * Indicates whether the field is required
	 * @type {Boolean}
	 */
	@property( { type: Boolean } ) accessor required = false;

	/**
	 * The value of the field, `true` if the switch is on, false otherwise.
	 * @type {Boolean}
	 */
	@property( { type: Boolean } ) accessor checked = false;

	/**
	 * Indicates whether the field is disabled
	 * @type {Boolean}
	 */
	@property( { type: Boolean } ) accessor disabled = false;

	/**
	 * Indicates whether the field is loading
	 * @type {Boolean}
	 */
	@property( { type: Boolean } ) accessor loading = false;

	/**
	 * The position of the label relative to the switch
	 * @type {String}
	 */
	@property( { type: String, attribute: 'label-placement' } )
	accessor labelPlacement = 'right';

	/**
	 * The label for the switch when it is on
	 * @type {String}
	 */
	@property( { type: String, attribute: 'label-on' } )
	accessor labelOn = escapeHTML( __( 'On', 'uncanny-automator' ) );

	/**
	 * The label for the switch when it is off
	 * @type {String}
	 */
	@property( { type: String, attribute: 'label-off' } )
	accessor labelOff = escapeHTML( __( 'Off', 'uncanny-automator' ) );

	/**
	 * The size of the switch
	 * @type {String}
	 */
	@property( { type: String, attribute: 'size' } )
	accessor size = 'regular';

	/**
	 * Renders the component's template.
	 *
	 * @return {TemplateResult} The template for the component.
	 */
	render() {
		return html`
			<label class="wrapper">
				<input 
					type="checkbox" 
					?required=${ this.required }
					?checked=${ this.checked }
					?disabled=${ this.disabled }
					@change=${ this.handleSwitchChange }
				/>

				<div 
					class="switch"
					?checked=${ this.checked }
					?disabled=${ this.disabled }
					?loading=${ this.loading }
					label-placement=${ this.labelPlacement }
					size=${ this.size }
				>
					<div class="switch-control">
						<div class="switch-control-inner"></div>
					</div>
					<div class="switch-label">
						${ this._templateLabel }
					</div>
				</div>
			</label>
		`;
	}

	/**
	 * Handles the change of the switch.
	 * This method waits for the `processChangeRequest` promise to resolve before proceeding.
	 *
	 * @return {Promise<void>}
	 */
	async handleSwitchChange() {
		// Temporarily disable the switch
		this.disabled = true;

		// Store the original checked state
		const originalChecked = this.checked;

		// Toggle the checked property
		this.checked = ! originalChecked;

		try {
			// Validate the change using the provided method
			const isValid = await this.processChangeRequest( this.checked );

			// Revert the change if validation fails
			if ( ! isValid ) {
				this.revertWithDelay( originalChecked );

				// Stop here
				return;
			}

			// Re-enable the switch
			this.disabled = false;

			// Dispatch the change event
			this.triggerChangeEvent();
		} catch ( error ) {
			// Revert the change if an error occurs
			this.revertWithDelay( originalChecked );
		}
	}

	/**
	 * Reverts the switch to its original state after a delay.
	 *
	 * @param {Boolean} checkStatus The status to revert to.
	 */
	revertWithDelay( checkStatus ) {
		setTimeout( () => {
			// Revert the switch to its original state
			this.checked = checkStatus;

			// Re-enable the switch
			this.disabled = false;
		}, 1000 );
	}

	/**
	 * Dispatches a custom `change` event with the current state of the switch.
	 */
	triggerChangeEvent() {
		this.dispatchEvent(
			new CustomEvent( 'change', {
				detail: {
					checked: this.checked,
				},
				bubbles: false,
				composed: true,
			} )
		);
	}

	/**
	 * Processes the user's request to change the switch state.
	 * This method performs validation and executes any necessary actions, such as making an API call, to determine whether the requested change should be applied.
	 *
	 * It is designed to be overridden in child classes or replaced by a custom callback, enabling customization of the validation and processing logic.
	 *
	 * @param {Boolean} isChecked The new state of the switch.
	 * @return {Promise<Boolean>} Resolves to `true` if the change is successfully applied, or `false` if the change is invalid or the process fails.
	 *
	 * @throws {Error} May throw an error if an unexpected issue occurs during the process.
	 */
	async processChangeRequest( isChecked ) {
		return new Promise( resolve => resolve( true ) );
	}

	/**
	 * Returns the label template based on the `checked` state.
	 *
	 * @return {TemplateResult|String} The label template.
	 * @private
	 */
	get _templateLabel() {
		return this.checked ? this.labelOn : this.labelOff;
	}
}
